"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import type { BlogComment } from "@/types";
import {
	useDeleteCommentMutation,
	useGetBlogCommentsQuery,
} from "@/features/blog/blogApiInjection";

import type { PartialCommentCollection } from "@/app/blogs/[...iden]/components/FeedbackSection/types";

import InfoSection from "@/app/blogs/[...iden]/components/FeedbackSection/InfoSection";

type Props = {
	blogId: string;
	createdAt: Date;
};

function useCommentsQuery(blogId: string) {
	const [page, setPage] = useState(1);
	const [data, setData] = useState<Map<string, BlogComment>>(new Map());
	const isMountedRef = useRef(true);
	const prevPageDataRef = useRef<{ page?: number; totalDocs?: number; docIds?: string } | null>(null);

	const [remove, { isLoading: isDeleting }] = useDeleteCommentMutation();
	
	// Memoize query args to prevent unnecessary refetches - use stable reference
	const queryArgs = useMemo(() => ({
		id: blogId,
		limit: 5,
		page,
	}), [blogId, page]);
	
	// Use skipToken pattern to prevent query if blogId is invalid, but more importantly
	// ensure the query only runs once per unique blogId+page combination
	const { data: pageData, isLoading } = useGetBlogCommentsQuery(queryArgs, {
		refetchOnMountOrArgChange: true, // Force refetch to ensure queries execute
		refetchOnFocus: false, // Prevent refetch when window regains focus
		refetchOnReconnect: false, // Prevent refetch on network reconnect
		// Ensure we don't poll
		pollingInterval: 0,
	});

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Memoize docIds to create a stable dependency - use ref to prevent unnecessary recalculations
	const prevDocIdsRef = useRef<string>('');
	const docIdsString = useMemo(() => {
		if (!pageData?.docs || pageData.docs.length === 0) {
			const empty = '';
			if (prevDocIdsRef.current !== empty) {
				prevDocIdsRef.current = empty;
			}
			return empty;
		}
		// Create stable string from doc IDs
		const newDocIds = pageData.docs.map((d: any) => d._id).sort().join(',');
		// Only update if actually different
		if (newDocIds !== prevDocIdsRef.current) {
			prevDocIdsRef.current = newDocIds;
		}
		return prevDocIdsRef.current;
	}, [pageData?.docs?.length, pageData?.docs?.map((d: any) => d._id).join(',')]);

	useEffect(() => {
		// Don't process if data is undefined or component is unmounted
		if (pageData === undefined || !isMountedRef.current) return;

		// Prevent infinite loops by checking if pageData actually changed
		const currentPage = pageData.page;
		const currentTotalDocs = pageData.totalDocs;
		const prevPage = prevPageDataRef.current?.page;
		const prevTotalDocs = prevPageDataRef.current?.totalDocs;

		// Only update if pageData actually changed
		if (currentPage === prevPage && currentTotalDocs === prevTotalDocs) {
			return;
		}

		// Also check if the docs array has actually changed by comparing IDs
		const prevDocIds = prevPageDataRef.current?.docIds;
		if (docIdsString === prevDocIds && currentPage === prevPage) {
			// Update ref but don't update state if docs haven't changed
			prevPageDataRef.current = { page: currentPage, totalDocs: currentTotalDocs, docIds: docIdsString };
			return;
		}

		// Update ref before state update to prevent race conditions
		prevPageDataRef.current = { page: currentPage, totalDocs: currentTotalDocs, docIds: docIdsString };

		// Use functional update to avoid dependency on `data`
		setData((prevData) => {
			const newData = new Map(page === 1 ? undefined : prevData);
			pageData.docs.forEach((x: any) => newData.set(x._id, x));
			return newData;
		});
		// Only depend on stable primitive values
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageData?.page, pageData?.totalDocs, docIdsString, page]);

	const transformed: PartialCommentCollection | undefined = useMemo(() => {
		if (pageData === undefined) return undefined;

		return {
			docs: Array.from(data.values()),
			totalDocs: pageData.totalDocs,
			totalPages: pageData.totalPages,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const handleDelete = useCallback(
		async (id: string) => {
			setPage(1);
			await remove({ blogId, commentId: id });

			const elem = document.getElementById("feedback-section");
			elem?.scrollIntoView({ behavior: "smooth" });
		},
		[blogId, remove],
	);

	const incrementPage = useCallback(() => {
		if (pageData === undefined || !pageData.nextPage) return;
		setPage(pageData.nextPage);
	}, [pageData]);

	return {
		page,
		incrementPage,
		isLoading,
		isDeleting,
		handleDelete,
		data: transformed,
	};
}

export default function FeedbackSection({ blogId }: Props) {
	return (
		<div id="feedback-section" className="px-8 lg:px-0">
			<InfoSection blogId={blogId} />
		</div>
	);
}
